import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "@libs/zapi-ui/ds/components/button";
import { Sheet, SheetContent, SheetTitle } from "@libs/zapi-ui/ds/components/sheet";
import { Spinner } from "@libs/zapi-ui/ds/components/spinner";
import type { TRisk, TRiskInput } from "@/zapi-frontend/shared/types/risks";
import { useCreateRisk, useUpdateRisk } from "./useRisks";
import { RiskFields, isRiskInputComplete, toRiskInput } from "./_RiskFields";

type TRiskFormSheetProps = {
  projectId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  risk: TRisk | null;
};

export function RiskFormSheet({ projectId, open, onOpenChange, risk }: TRiskFormSheetProps) {
  const { t } = useTranslation(["projectHome", "common"]);
  const createRisk = useCreateRisk(projectId);
  const updateRisk = useUpdateRisk(projectId);

  const [input, setInput] = useState<TRiskInput>(toRiskInput());

  useEffect(() => {
    if (open) setInput(toRiskInput(risk));
  }, [open, risk]);

  const isSaving = createRisk.isPending || updateRisk.isPending;
  const canSubmit = isRiskInputComplete(input);

  const handleSubmit = async () => {
    if (!canSubmit) return;
    const trimmed: TRiskInput = {
      title: input.title.trim(),
      whatMustHoldTrue: input.whatMustHoldTrue.trim(),
      whyItMatters: input.whyItMatters.trim(),
      impact: input.impact,
      uncertainty: input.uncertainty
    };
    try {
      if (risk) {
        await updateRisk.mutateAsync({ riskId: risk.id, input: trimmed });
        toast.success(t("risks.updated"));
      } else {
        await createRisk.mutateAsync(trimmed);
        toast.success(t("risks.created"));
      }
      onOpenChange(false);
    } catch {
      toast.error(t("risks.saveFailed"));
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        closeLabel={t("common:close")}
        className="flex w-[440px] max-w-full flex-col gap-0 p-0 sm:max-w-[440px]"
      >
        <div className="flex h-full flex-col gap-6 overflow-y-auto p-5 custom-thin-scrollbar">
          <SheetTitle className="font-medium tracking-[-0.2px]">
            {risk ? t("risks.editTitle") : t("risks.addTitle")}
          </SheetTitle>

          <RiskFields value={input} onChange={setInput} />

          <div>
            <Button type="button" onClick={handleSubmit} disabled={isSaving || !canSubmit}>
              {isSaving ? (
                <>
                  <Spinner data-icon="inline-start" aria-label={t("common:loading")} />
                  {t("common:loading")}
                </>
              ) : risk ? (
                t("risks.save")
              ) : (
                t("risks.addTitle")
              )}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
